<template>
  <div class="fixed top-4 right-4 z-[60] space-y-2">
    <TransitionGroup
      enter-active-class="transform ease-out duration-300 transition"
      enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-for="alert in alerts"
        :key="alert.id"
        class="w-[400px] bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"
        role="alert"
      >
        <div class="p-4">
          <div class="flex items-start space-x-3">
            <div class="flex-shrink-0 pt-0.5">
              <CheckCircleIcon
                v-if="alert.type === 'success'"
                class="h-5 w-5 text-green-400"
                aria-hidden="true"
              />
              <XCircleIcon
                v-else
                class="h-5 w-5 text-red-400"
                aria-hidden="true"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p
                class="text-sm font-medium text-gray-900 dark:text-white break-words"
              >
                {{ alert.message }}
              </p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {{ t("dashboard.alerts.shipment.momentAgo") }}
              </p>
            </div>
            <div class="flex-shrink-0 self-start ml-4">
              <button
                type="button"
                @click="removeAlert(alert.id)"
                class="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <span class="sr-only">{{
                  t("dashboard.alerts.shipment.close")
                }}</span>
                <XMarkIcon class="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { eventBus } from "../../utils/eventBus";
import { useAuthStore } from "../../stores/auth";
import { doc, collection, getDoc } from "firebase/firestore";
import { FirebaseProvider } from "../../infrastructure/providers/FirebaseProvider";
import { CheckCircleIcon, XMarkIcon, XCircleIcon } from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Alert {
  id: string;
  message: string;
  timestamp: number;
  shipmentId?: string;
  type: 'success' | 'error';
}

const alerts = ref<Alert[]>([]);
const authStore = useAuthStore();

// Control de alertas duplicadas
const isRecentDuplicate = (shipmentId: string) => {
  const now = Date.now();
  return alerts.value.some(
    (alert) => alert.shipmentId === shipmentId && now - alert.timestamp < 2000
  );
};

const addAlert = (message: string, shipmentId?: string, type: 'success' | 'error' = 'success') => {
  const id = Math.random().toString(36).substr(2, 9);
  alerts.value.push({
    id,
    message,
    timestamp: Date.now(),
    shipmentId,
    type,
  });

  // Auto-remover después de 15 segundos
  setTimeout(() => {
    removeAlert(id);
  }, 15000);
};

const removeAlert = (id: string) => {
  const index = alerts.value.findIndex((alert) => alert.id === id);
  if (index !== -1) {
    alerts.value.splice(index, 1);
  }
};

const showNotification = async (data: {
  userId: string;
  userName: string;
  shipmentNumber: string;
  shipmentId: string;
  description?: string;
  changes: Array<{
    field: string;
    oldValue: any;
    newValue: any;
  }>;
}) => {
  if (data.userId === authStore.user?.uid) {
    return;
  }
  // Verificar si es una alerta duplicada reciente
  if (isRecentDuplicate(data.shipmentId)) {
    return;
  }

  // Si hay una descripción personalizada, usarla directamente
  if (data.description) {
    let translatedDescription;
    let matched = false;

    // Extraer el número del contenedor o paquete si existe
    const containerMatch = data.description.match(
      /^(Creó un contenedor con número) (.+)$/
    );
    const packageMatch = data.description.match(
      /^(Creó un paquete con número de referencia) (.+)$/
    );
    const packageNoRefMatch = data.description.match(
      /^Creó un paquete sin número de referencia$/
    );

    if (containerMatch) {
      const [, baseDescription, number] = containerMatch;
      translatedDescription = t(
        "dashboard.shipments.changes.containerCreated",
        {
          number,
        }
      );
      matched = true;
    } else if (packageMatch) {
      const [, baseDescription, number] = packageMatch;
      translatedDescription = t("dashboard.shipments.changes.packageCreated", {
        number,
      });
      matched = true;
    } else if (packageNoRefMatch) {
      translatedDescription = t(
        "dashboard.shipments.changes.packageCreatedNoRef"
      );
      matched = true;
    }

    // Si no es un contenedor o paquete, verificar otros patrones
    if (!matched) {
      const patterns = {
        deleteContainer: /(Eliminó el (?:contenedor)) ([A-Z0-9]+)/,
        updateContainer: /(Actualizó el contenedor con número) ([A-Z0-9]+)/,
        createContainer: /(Creó un contenedor con número) ([A-Z0-9]+)/,
        createPackageWithRef:
          /(Creó un paquete con número de referencia) ([A-Z0-9]+)/,
        deletePackageWithRef:
          /(Eliminó un paquete con número de referencia) ([A-Z0-9]+)/,
        updatePackageWithRef:
          /(Actualizó el paquete con número de referencia) ([A-Z0-9]+)/,
      };

      for (const [key, pattern] of Object.entries(patterns)) {
        const matches = data.description.match(pattern);
        if (matches) {
          const [, baseDescription, number] = matches;
          const translationKey =
            key === "deleteContainer"
              ? "containerDeleted"
              : key === "updateContainer"
              ? "containerUpdated"
              : key === "createPackageWithRef"
              ? "packageCreated"
              : key === "deletePackageWithRef"
              ? "packageDeleted"
              : "packageUpdated";

          translatedDescription = t(
            `dashboard.shipments.changes.${translationKey}`,
            {
              number,
            }
          );
          matched = true;
          break;
        }
      }
    }

    // Si no coincide con ningún patrón, usar la descripción completa
    if (!matched) {
      translatedDescription = t(
        `dashboard.shipments.changes.${data.description}`
      );
    }

    // Si la descripción contiene "contenedor" o "paquete", agregar "en el embarque"
    const isContainerOrPackage =
      translatedDescription.toLowerCase().includes("contenedor") ||
      translatedDescription.toLowerCase().includes("paquete");
    const message = isContainerOrPackage
      ? t("dashboard.alerts.shipment.updates.customWithShipment", {
          userName: data.userName,
          description: translatedDescription,
          shipmentNumber: data.shipmentNumber,
        })
      : t("dashboard.alerts.shipment.updates.customGeneric", {
          userName: data.userName,
          description: translatedDescription,
          shipmentNumber: data.shipmentNumber,
        });

    addAlert(message, data.shipmentId);
    return;
  }

  // Si no hay descripción, mantener la lógica existente como fallback
  try {
    const db = FirebaseProvider.getInstance().getFirestore();
    const companyId = localStorage.getItem("selectedCompanyId");

    const changes = data.changes || [];
    const firstChange = changes[0]?.field || "";

    let message = "";

    if (firstChange.startsWith("container.")) {
      const containerId = firstChange.split(".")[1];
      const containerRef = doc(
        collection(
          doc(
            collection(db, "Companies", companyId!, "shipments"),
            data.shipmentId
          ),
          "containers"
        ),
        containerId
      );
      const containerDoc = await getDoc(containerRef);
      const containerNumber = containerDoc.exists()
        ? containerDoc.data().number
        : containerId;

      message = t("dashboard.alerts.shipment.updates.container", {
        userName: data.userName,
        containerNumber,
        shipmentNumber: data.shipmentNumber,
      });
    } else if (firstChange.startsWith("package.")) {
      const packageId = firstChange.split(".")[1];
      const packageRef = doc(
        collection(
          doc(
            collection(db, "Companies", companyId!, "shipments"),
            data.shipmentId
          ),
          "packages"
        ),
        packageId
      );
      const packageDoc = await getDoc(packageRef);
      const packageReference = packageDoc.exists()
        ? packageDoc.data().reference
        : packageId;

      message = t("dashboard.alerts.shipment.updates.package", {
        userName: data.userName,
        packageReference,
        shipmentNumber: data.shipmentNumber,
      });
    } else {
      message = t("dashboard.alerts.shipment.updates.generic", {
        userName: data.userName,
        shipmentNumber: data.shipmentNumber,
      });
    }

    addAlert(message, data.shipmentId, 'success');
  } catch (error) {
    const message = t("dashboard.alerts.shipment.updates.generic", {
      userName: data.userName,
      shipmentNumber: data.shipmentNumber,
    });
    addAlert(message, data.shipmentId, 'success');
  }
};

const showAlert = (data: { type: 'success' | 'error'; message: string }) => {
  addAlert(data.message, undefined, data.type);
};

onMounted(() => {
  eventBus.on("shipment-updated", showNotification);
  eventBus.on("show-alert", showAlert);
});

onUnmounted(() => {
  eventBus.off("shipment-updated", showNotification);
  eventBus.off("show-alert", showAlert);
});
</script>

<style scoped>
.space-y-2 > :not([hidden]) ~ :not([hidden]) {
  --tw-space-y-reverse: 0;
  margin-top: calc(0.5rem * calc(1 - var(--tw-space-y-reverse)));
  margin-bottom: calc(0.5rem * var(--tw-space-y-reverse));
}
</style>
